<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useQuery, useQueryClient } from "@tanstack/vue-query";

import {
  assignObservation,
  changeObservationStatus,
  draftObservationAction,
  getMe,
  getObservation,
  getObservationHistory,
  listWorkers,
  reviewObservation,
  updateObservation,
  type RegisterReviewFinding,
} from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import PhotoGallery from "@/components/PhotoGallery.vue";

const route = useRoute();
const id = computed(() => route.params.observationId as string);
const queryClient = useQueryClient();

// Clients get a read-only record (view + history only).
const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
const isClient = computed(() => (me.value?.roles ?? []).includes("client"));

const { data, isPending, isError, error } = useQuery({
  queryKey: ["observation", id],
  queryFn: () => getObservation(id.value),
});
const { data: history } = useQuery({
  queryKey: ["observation-history", id],
  queryFn: () => getObservationHistory(id.value),
});
const { data: workers } = useQuery({
  queryKey: ["workers", true],
  queryFn: () => listWorkers(true),
});
// App-enabled workers (those with a linked login) can action the assignment in
// the field app, so list them first and flag them in the picker.
const assignableWorkers = computed(() =>
  [...(workers.value ?? [])].sort(
    (a, b) =>
      (b.user_id ? 1 : 0) - (a.user_id ? 1 : 0) || a.name.localeCompare(b.name),
  ),
);

const busy = ref(false);
const actionError = ref<string | null>(null);

// Editable action-taken (mitigation) — seeded from the record.
const actionText = ref("");
watch(data, (d) => {
  if (d) actionText.value = d.action_taken ?? "";
});

async function act(fn: () => Promise<void>): Promise<void> {
  busy.value = true;
  actionError.value = null;
  try {
    await fn();
    await queryClient.invalidateQueries({ queryKey: ["observation", id] });
    await queryClient.invalidateQueries({ queryKey: ["observation-history", id] });
    await queryClient.invalidateQueries({ queryKey: ["observations"] });
  } catch (e) {
    actionError.value = e instanceof Error ? e.message : String(e);
  } finally {
    busy.value = false;
  }
}

function onAssign(e: Event): void {
  const wid = (e.target as HTMLSelectElement).value;
  if (!wid) return;
  const w = (workers.value ?? []).find((x) => x.id === wid);
  void act(() => assignObservation(id.value, wid, w?.name));
}
function setStatus(status: "open" | "in_progress" | "closed"): void {
  void act(() => changeObservationStatus(id.value, status));
}
function saveAction(): void {
  void act(() => updateObservation(id.value, { action_taken: actionText.value.trim() }));
}

// --- AI action draft + pre-close review (Phase 3) ---
const draftingAction = ref(false);
async function runDraftAction(): Promise<void> {
  draftingAction.value = true;
  try {
    const r = await draftObservationAction(id.value);
    actionText.value = r.draft;
  } catch (e) {
    actionError.value = e instanceof Error ? e.message : String(e);
  } finally {
    draftingAction.value = false;
  }
}

const reviewBusy = ref(false);
const reviewMsg = ref<string | null>(null);
const reviewFindings = ref<RegisterReviewFinding[] | null>(null);
async function runReview(): Promise<void> {
  reviewBusy.value = true;
  reviewMsg.value = null;
  try {
    const r = await reviewObservation(id.value);
    reviewFindings.value = r.findings;
    reviewMsg.value = r.ok
      ? "Looks complete — nothing flagged."
      : `${r.findings.length} thing(s) to check before closing.`;
  } catch (e) {
    reviewMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    reviewBusy.value = false;
  }
}

const fmt = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  }) : "—";
const kindLabel = (k: string): string =>
  k === "close_call" ? "Close call" : "Good practice";
</script>

<template>
  <PageLayout>
    <RouterLink
      :to="{ name: 'observations', params: { projectId: $route.params.projectId } }"
      class="text-sm text-neutral-500 hover:text-neutral-900"
    >
      ← Close calls &amp; good practice
    </RouterLink>

    <div
      v-if="isPending"
      class="mt-4 text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="mt-4 rounded-md bg-semantic-danger/10 p-4 text-semantic-danger"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="data"
      class="mt-4 grid gap-6"
    >
      <header class="flex items-start justify-between gap-4">
        <div>
          <h1 class="text-xl font-semibold">
            <span
              class="mr-1 rounded-full px-2 py-0.5 align-middle text-xs"
              :class="data.kind === 'close_call'
                ? 'bg-semantic-warning/10 text-semantic-warning'
                : 'bg-semantic-success/10 text-semantic-success'"
            >{{ kindLabel(data.kind) }}</span>
          </h1>
          <p class="mt-1 text-sm text-neutral-500">
            Raised {{ fmt(data.raised_at) }}
            <span v-if="data.raised_by_name"> by {{ data.raised_by_name }}</span>
            <span v-if="data.location_ref"> · 📍 {{ data.location_ref }}</span>
          </p>
        </div>
        <span
          class="shrink-0 rounded-full px-3 py-1 text-xs font-medium capitalize"
          :class="data.status === 'closed' ? 'bg-semantic-success/10 text-semantic-success'
            : data.status === 'in_progress' ? 'bg-semantic-warning/10 text-semantic-warning'
              : 'bg-neutral-100 text-neutral-500'"
        >{{ data.status.replace("_", " ") }}</span>
      </header>

      <p
        v-if="actionError"
        class="rounded-md bg-semantic-danger/10 p-3 text-sm text-semantic-danger"
      >
        {{ actionError }}
      </p>

      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-2 font-semibold">
          What happened
        </h2>
        <p class="whitespace-pre-line text-sm text-neutral-800">
          {{ data.description }}
        </p>
        <p
          v-if="data.observation_type"
          class="mt-2 text-sm text-neutral-500"
        >
          Type: {{ data.observation_type }}
        </p>
      </section>

      <!-- Photo evidence -->
      <PhotoGallery
        :file-ids="data.photo_ids"
        title="Photo evidence"
      />

      <!-- Manage / close the loop -->
      <section
        v-if="!isClient"
        class="rounded-xl border border-neutral-200 bg-neutral-0 p-6"
      >
        <h2 class="mb-4 font-semibold">
          Action &amp; close-out
        </h2>
        <div class="grid gap-4 sm:grid-cols-2">
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Assigned to</span>
            <select
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
              :value="data.assigned_to ?? ''"
              :disabled="busy"
              @change="onAssign"
            >
              <option value="">
                Unassigned
              </option>
              <option
                v-for="w in assignableWorkers"
                :key="w.id"
                :value="w.id"
              >
                {{ w.name }}{{ w.user_id ? " — app user" : "" }}
              </option>
            </select>
          </label>
          <div class="grid gap-1 text-sm">
            <span class="text-neutral-500">Status</span>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="s in (['open', 'in_progress', 'closed'] as const)"
                :key="s"
                type="button"
                class="rounded-md px-3 py-1.5 text-sm font-medium capitalize"
                :class="data.status === s
                  ? 'bg-brand-primary text-brand-primary-on'
                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'"
                :disabled="busy"
                @click="setStatus(s)"
              >
                {{ s.replace("_", " ") }}
              </button>
            </div>
          </div>
        </div>

        <label class="mt-4 grid gap-1 text-sm">
          <div class="flex items-center justify-between">
            <span class="text-neutral-500">Action taken (what was done to close the loop)</span>
            <div class="flex items-center gap-2">
              <button
                v-if="data.kind === 'close_call'"
                type="button"
                class="rounded-md border border-brand-primary/40 bg-brand-primary/5 px-2.5 py-1 text-xs font-medium text-brand-primary hover:bg-brand-primary/10 disabled:opacity-50"
                :disabled="draftingAction"
                title="Suggest a corrective action from the description"
                @click="runDraftAction"
              >
                {{ draftingAction ? "Suggesting…" : "✨ Suggest action" }}
              </button>
              <button
                type="button"
                class="rounded-md border border-neutral-300 px-2.5 py-1 text-xs font-medium hover:bg-neutral-50 disabled:opacity-50"
                :disabled="reviewBusy"
                @click="runReview"
              >
                {{ reviewBusy ? "Checking…" : "Pre-close review" }}
              </button>
            </div>
          </div>
          <textarea
            v-model="actionText"
            rows="3"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          />
        </label>
        <p
          v-if="reviewMsg"
          class="mt-2 text-sm"
          :class="reviewFindings && reviewFindings.length
            ? 'text-neutral-700' : 'text-semantic-success'"
        >
          {{ reviewMsg }}
        </p>
        <ul
          v-if="reviewFindings && reviewFindings.length"
          class="mt-2 grid gap-1.5"
        >
          <li
            v-for="(f, i) in reviewFindings"
            :key="i"
            class="flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
            :class="f.severity === 'warning'
              ? 'border-semantic-warning/40 bg-semantic-warning/5'
              : 'border-neutral-200 bg-neutral-50'"
          >
            <span class="shrink-0">{{ f.severity === "warning" ? "⚠️" : "💡" }}</span>
            <span>{{ f.message }}</span>
          </li>
        </ul>
        <div class="mt-2">
          <button
            type="button"
            class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
            :disabled="busy"
            @click="saveAction"
          >
            Save action
          </button>
        </div>
      </section>

      <!-- Record + history -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-3 font-semibold">
          Record
        </h2>
        <dl class="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
          <dt class="text-neutral-500">
            Assigned to
          </dt>
          <dd>{{ data.assigned_to_name ?? "Unassigned" }}</dd>
          <template v-if="data.action_taken">
            <dt class="text-neutral-500">
              Action taken
            </dt>
            <dd class="whitespace-pre-line">
              {{ data.action_taken }}
            </dd>
          </template>
          <template v-if="data.closed_at">
            <dt class="text-neutral-500">
              Closed
            </dt>
            <dd>{{ fmt(data.closed_at) }}<span v-if="data.closed_by_name"> · {{ data.closed_by_name }}</span></dd>
          </template>
          <dt class="text-neutral-500">
            Last updated
          </dt>
          <dd>{{ fmt(data.last_updated_at) }}<span v-if="data.last_updated_by_name"> · {{ data.last_updated_by_name }}</span></dd>
        </dl>

        <h3 class="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-neutral-400">
          History
        </h3>
        <ol class="grid gap-1 text-sm">
          <li
            v-for="h in history ?? []"
            :key="h.seq"
            class="flex flex-wrap gap-x-2 text-neutral-600"
          >
            <span class="font-medium text-neutral-800">{{ h.label }}</span>
            <span class="text-neutral-400">·</span>
            <span>{{ h.actor_name ?? "—" }}</span>
            <span class="text-neutral-400">·</span>
            <span class="text-neutral-400">{{ fmt(h.at) }}</span>
          </li>
        </ol>
      </section>
    </div>
  </PageLayout>
</template>
